'use client'

import { useEffect, useRef } from 'react'

import { getClientAnalytics } from '../client-instance'

export interface AnalyticsIdentity {
  /**
   * The identity Datadog is keyed on (`@usr.id`) — the **member** id.
   *
   * Deliberately the member id, not the session user id, to stay continuous
   * with RUM sessions, dashboards, and replay lookups already recorded under
   * it. The session user id travels as the `user_id` property below.
   */
  memberId?: string
  userId?: string
  email?: string
  role?: string | null
  organizationId?: string
}

/**
 * Identifies the user in analytics and resets identity on logout.
 * Should be called once in an authenticated layout/provider.
 */
export function useAnalyticsIdentity(
  identity: AnalyticsIdentity | null | undefined,
): void {
  const identifiedRef = useRef(false)

  const { memberId, userId, email, role, organizationId } = identity ?? {}

  useEffect(() => {
    if (!memberId) {
      if (identifiedRef.current) {
        getClientAnalytics().reset()
        identifiedRef.current = false
      }
      return
    }

    const analytics = getClientAnalytics()
    analytics.identify(memberId)
    analytics.setUserProperties({
      user_id: userId,
      email,
      user_role: role ?? undefined,
      organization_id: organizationId,
    })
    identifiedRef.current = true
  }, [memberId, userId, email, role, organizationId])
}
